/**
 * usePersonaMemory — Hook for managing AI persona memory (056-ai-persona-evolution, Spec 1)
 * Handles list/delete/chronicle WS commands and incoming events.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { UseMultiplayerReturn } from './useMultiplayer';
import type { PersonaNote, ServerMessage } from '@roll1/shared';

export interface UsePersonaMemoryReturn {
  notes: PersonaNote[];
  isLoading: boolean;
  /** Request the list of persona notes from server */
  loadNotes: () => void;
  /** Delete a persona note by ID */
  deleteNote: (noteId: string) => void;
  /** Request chronicle generation (optionally as epilogue) */
  requestChronicle: (isEpilogue?: boolean) => void;
  /** Chronicle streaming text (accumulated chunks) */
  chronicleText: string;
  /** Whether chronicle is currently streaming */
  isChronicleStreaming: boolean;
  /** Full chronicle text after generation completes */
  chronicleResult: string | null;
  /** Clear chronicle state */
  clearChronicle: () => void;
}

export function usePersonaMemory(mp: UseMultiplayerReturn): UsePersonaMemoryReturn {
  const [notes, setNotes] = useState<PersonaNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chronicleText, setChronicleText] = useState('');
  const [isChronicleStreaming, setIsChronicleStreaming] = useState(false);
  const [chronicleResult, setChronicleResult] = useState<string | null>(null);

  const loadNotes = useCallback(() => {
    setIsLoading(true);
    mp.send({ type: 'persona-memory:list' });
  }, [mp]);

  const deleteNote = useCallback((noteId: string) => {
    mp.send({ type: 'persona-memory:delete', noteId });
    // Optimistic removal
    setNotes(prev => prev.filter(n => n.id !== noteId));
  }, [mp]);

  const requestChronicle = useCallback((isEpilogue?: boolean) => {
    setChronicleText('');
    setChronicleResult(null);
    setIsChronicleStreaming(true);
    mp.send({ type: 'persona-memory:chronicle', ...(isEpilogue ? { isEpilogue: true } : {}) });
  }, [mp]);

  const clearChronicle = useCallback(() => {
    setChronicleText('');
    setChronicleResult(null);
    setIsChronicleStreaming(false);
  }, []);

  // Listen for server events
  useEffect(() => {
    const unsub = mp.onMessage((msg: ServerMessage) => {
      switch (msg.type) {
        case 'persona-memory:listed': {
          const data = msg as ServerMessage & { notes: PersonaNote[] };
          setNotes(data.notes);
          setIsLoading(false);
          break;
        }
        case 'persona-memory:deleted': {
          const data = msg as ServerMessage & { noteId: string };
          setNotes(prev => prev.filter(n => n.id !== data.noteId));
          break;
        }
        case 'persona-memory:chronicle-chunk': {
          const data = msg as ServerMessage & { text: string };
          setChronicleText(prev => prev + data.text);
          break;
        }
        case 'persona-memory:chronicle-done': {
          const data = msg as ServerMessage & { fullText: string };
          setIsChronicleStreaming(false);
          setChronicleResult(data.fullText);
          if (data.fullText) {
            setChronicleText(data.fullText);
          }
          break;
        }
      }
    });
    return unsub;
  }, [mp]);

  return {
    notes,
    isLoading,
    loadNotes,
    deleteNote,
    requestChronicle,
    chronicleText,
    isChronicleStreaming,
    chronicleResult,
    clearChronicle,
  };
}
